import { useParams } from "react-router-dom";

// React Bootstrap components
import { Card, Row, Col } from "react-bootstrap";

// Components
import TabCardHeader from "../../../ui/TabCardHeader";
import Field from "../../../ui/Field";
import AccessDenied from "../../../ui/AccessDenied";

// API calls
import { useGetDeminimisByProjekt } from "../../../../services/ProjectDetails/financeAPI";

// Auth
import RoleGuard from "../../../../auth/RoleGuard";
import { MODULES, ROLE_GROUPS } from "../../../../auth/roles";
import useRoleCheck from "../../../../auth/useRoleCheck";
import useClanicaCheck from "../../../../auth/useClanicaCheck";

// Context
import { useProjectDetailsContext } from "../../../../context/ProjectDetailsContext";

// Utils
import currencyFormatToEUR from "../../../utils/currencyFormatToEUR";

const DeminimisProjectDetails = () => {
  const { id } = useParams();
  const { project } = useProjectDetailsContext();
  const { isEditor, isFakultetniSkrbnik } = useRoleCheck(MODULES.PROJEKTI);

  const { isUserClanicaInList } = useClanicaCheck(null, project?.clanice ?? []);

  // Editor always sees deminimis; fakultetni skrbnik only if their clanica is on this project
  const canViewDeminimis =
    isEditor || (isFakultetniSkrbnik && isUserClanicaInList);

  const { data: deminimisData, isLoading } = useGetDeminimisByProjekt(id);

  // Find SKUPAJ entry for total and filter it out from regular data
  const skupajEntry = deminimisData?.find((item) => item.clanica === "SKUPAJ");
  const totalPomoc = skupajEntry?.visinaOdobrenePomoci || 0;

  // Filter out SKUPAJ and group remaining data by clanica
  const filteredData =
    deminimisData?.filter((item) => item.clanica !== "SKUPAJ") || [];
  const groupedData = filteredData.reduce((acc, item) => {
    const clanica = item.clanica;
    if (!acc[clanica]) {
      acc[clanica] = [];
    }
    acc[clanica].push(item);
    return acc;
  }, {});

  if (isLoading) {
    return (
      <Card className="mb-4">
        <TabCardHeader
          title="De minimis odobrena pomoč"
          showEditButton={false}
        />
        <div className="px-4 pt-4 pb-2">
          <h6 className="form-section-heading">Celoten projekt</h6>
          <p>Nalaganje...</p>
        </div>
      </Card>
    );
  }

  return (
    <RoleGuard
      moduleKey={MODULES.PROJEKTI}
      roles={ROLE_GROUPS.GLOBALNI_AND_FAKULTETNI}
      fallback={<AccessDenied />}
    >
      {canViewDeminimis && (
        <Card className="mb-4">
          <TabCardHeader
            title="De minimis odobrena pomoč"
            showEditButton={false}
          />
          <div className="px-4 pt-4 pb-2">
            <Row>
              <Col md={5}>
                <h6 className="form-section-heading">Celoten projekt</h6>
                <Field
                  label="Višina odobrene pomoči"
                  value={currencyFormatToEUR(totalPomoc)}
                />
              </Col>
            </Row>
            <hr className="form-section-divider" />
            <h6 className="form-section-heading">
              Podatki ki se nanašajo na članice UM
            </h6>
            {groupedData && Object.keys(groupedData).length > 0 ? (
              Object.entries(groupedData).map(([clanica, items]) => (
                <Card key={clanica} className="mb-3 mt-3">
                  <div className="d-flex align-items-center justify-content-between px-3 pt-3">
                    <h5 className="mb-0">{clanica}</h5>
                  </div>
                  <div className="px-3 pb-3 pt-2">
                    {items.map((item, index) => (
                      <div key={index}>
                        <Row>
                          <Col md={4}>
                            <Field
                              label="Datum odobritve"
                              value={
                                item.datumOdobritve
                                  ? new Date(
                                      item.datumOdobritve,
                                    ).toLocaleDateString("sl-SI")
                                  : "-"
                              }
                            />
                          </Col>
                          <Col md={4}>
                            <Field
                              label="Dajalec"
                              value={item.dajalec || "Ni podatka"}
                            />
                          </Col>
                          <Col md={4}>
                            <Field
                              label="Višina odobrene pomoči"
                              value={currencyFormatToEUR(
                                item.visinaOdobrenePomoci,
                              )}
                            />
                          </Col>
                        </Row>
                        {index < items.length - 1 && <hr className="my-2" />}
                      </div>
                    ))}
                  </div>
                </Card>
              ))
            ) : (
              <p className="text-muted">
                Ni podatkov o De minimis pomoči za ta projekt.
              </p>
            )}
          </div>
        </Card>
      )}
    </RoleGuard>
  );
};

export default DeminimisProjectDetails;
